import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ForceGraph2D } from "react-force-graph";
import { useTheme } from "next-themes";
import { Scan, RotateCcw } from "lucide-react";
import { useSpring, animated, to as rto } from "@react-spring/web";
import { useOrg360GraphData, type Org360IncludeFlags } from "@/hooks/org360/useOrg360GraphData";
import { useOrg360NodeDetail } from "@/hooks/org360/useOrg360NodeDetail";
import { nodeColor, nodeRadius, linkColor, HUB_LABEL_COUNT, EDGE_TYPE_GROUPS } from "./org360Constants";
import { Org360Minimap } from "./Org360Minimap";
import type { Org360Node, Org360Link } from "./org360Types";
import { cn } from "@/lib/utils";

const linkEndId = (end: string | Org360Node): string =>
  typeof end === "object" ? end.id : end;

function ToolbarTooltip({ label, children }: { label: string; children: React.ReactNode }) {
  const [visible, setVisible] = useState(false);
  const springs = useSpring({
    opacity: visible ? 1 : 0,
    y: visible ? 0 : -4,
    scale: visible ? 1 : 0.88,
    config: { tension: 420, friction: 24 },
  });
  return (
    <div className="relative" onMouseEnter={() => setVisible(true)} onMouseLeave={() => setVisible(false)}>
      {children}
      <animated.div
        className="absolute top-full right-0 mt-2 pointer-events-none z-50"
        style={{
          transform: rto(
            [springs.y, springs.scale],
            (y, s) => `translateY(${y}px) scale(${s})`,
          ),
          opacity: springs.opacity,
        }}
      >
        <div className="whitespace-nowrap px-2.5 py-1 rounded-lg
          bg-foreground/90 text-background text-[11px] font-medium font-mono
          shadow-[0_2px_8px_hsl(var(--foreground)/0.15)]">
          {label}
        </div>
      </animated.div>
    </div>
  );
}

interface Org360GraphProps {
  selectedType?: string;
  selectedNodeId?: string | null;
  navigateToNodeId?: string | null;
  hoveredRelationNodeId?: string | null;
  deepLinkNode?: string;
  hiddenEdgeTypes?: Set<string>;
  includeFlags?: Org360IncludeFlags;
  onNodeSelect: (node: Org360Node | null) => void;
  onNodeHover?: (id: string | null) => void;
  onCloseAll?: () => void;
  onReset?: () => void;
  /** Reports the ids of managed nodes currently revealed on the canvas so the
   *  rest of the UI (inspector relation rows) can stay in sync. */
  onRevealedIdsChange?: (ids: string[]) => void;
}

export function Org360Graph({ selectedType, selectedNodeId, navigateToNodeId, hoveredRelationNodeId, deepLinkNode, hiddenEdgeTypes, includeFlags, onNodeSelect, onNodeHover, onCloseAll, onReset, onRevealedIdsChange }: Org360GraphProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const graphRef = useRef<any>(null);
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";

  const [dimensions, setDimensions] = useState({ width: 800, height: 600 });
  const [hoveredNodeId, setHoveredNodeId] = useState<string | null>(null);
  const [hoveredLink, setHoveredLink] = useState<boolean>(false);
  const [isMouseDown, setIsMouseDown] = useState(false);
  const [engineStopped, setEngineStopped] = useState(false);
  const deepLinkApplied = useRef(false);
  const forcesApplied = useRef(false);
  const dragOrigin = useRef<{ x: number; y: number } | null>(null);
  const dragFiredRef = useRef(false);
  const userHasInteracted = useRef(false);

  // Cursor management — applied directly to the canvas element
  useEffect(() => {
    const canvas = containerRef.current?.querySelector("canvas");
    if (!canvas) return;
    if (hoveredNodeId) {
      canvas.style.cursor = "pointer";
    } else if (hoveredLink) {
      canvas.style.cursor = "crosshair";
    } else if (isMouseDown) {
      // grabbing only while actively panning the canvas
      canvas.style.cursor = "grabbing";
    } else {
      // default arrow at rest — reads as "click to select" rather than the
      // open-hand "drag" affordance, which made nodes feel draggable
      canvas.style.cursor = "default";
    }
  }, [hoveredNodeId, hoveredLink, isMouseDown]);

  // Reset minimap visibility when graph data changes
  useEffect(() => {
    setEngineStopped(false);
  }, [selectedType]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const { width, height } = entries[0].contentRect;
      setDimensions({ width, height });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const { data: graphData, isLoading, refetch } = useOrg360GraphData(selectedType, includeFlags);

  // Managed-package components are excluded from the default graph by the
  // server. When a node is opened we reveal the managed components it actually
  // connects to (and only those), accumulating them across clicks so earlier
  // reveals stay on screen. Reset whenever the underlying graph changes.
  const { data: nodeDetail } = useOrg360NodeDetail(selectedNodeId ?? null);
  const [injected, setInjected] = useState<{ nodes: Org360Node[]; links: Org360Link[] }>({
    nodes: [],
    links: [],
  });

  useEffect(() => {
    setInjected({ nodes: [], links: [] });
  }, [selectedType, graphData]);

  useEffect(() => {
    if (!selectedNodeId || !nodeDetail) return;
    const conns = [
      ...(nodeDetail.incoming ?? []).map((c) => ({ ...c, dir: "in" as const })),
      ...(nodeDetail.outgoing ?? []).map((c) => ({ ...c, dir: "out" as const })),
    ].filter((c) => c.id && c.is_managed);
    if (conns.length === 0) return;

    setInjected((prev) => {
      const baseNodeIds = new Set((graphData?.nodes ?? []).map((n) => n.id));
      // The anchor must be visible in the current graph for its managed links to
      // attach to something — skip nodes opened from the context table that
      // aren't part of the active (possibly type-filtered) graph.
      if (!baseNodeIds.has(selectedNodeId)) return prev;
      const haveNodes = new Set<string>([...baseNodeIds, ...prev.nodes.map((n) => n.id)]);
      const linkKey = (s: string, t: string, r: string) => `${s}|${t}|${r}`;
      const haveLinks = new Set<string>([
        ...(graphData?.edges ?? []).map((e) =>
          linkKey(linkEndId(e.source), linkEndId(e.target), e.rel),
        ),
        ...prev.links.map((e) => linkKey(linkEndId(e.source), linkEndId(e.target), e.rel)),
      ]);

      const nextNodes = [...prev.nodes];
      const nextLinks = [...prev.links];
      let changed = false;
      for (const c of conns) {
        if (!haveNodes.has(c.id)) {
          nextNodes.push({ id: c.id, label: c.label, type: c.type, is_managed: true });
          haveNodes.add(c.id);
          changed = true;
        }
        const src = c.dir === "out" ? selectedNodeId : c.id;
        const tgt = c.dir === "out" ? c.id : selectedNodeId;
        const k = linkKey(src, tgt, c.rel);
        if (!haveLinks.has(k)) {
          nextLinks.push({ source: src, target: tgt, rel: c.rel });
          haveLinks.add(k);
          changed = true;
        }
      }
      return changed ? { nodes: nextNodes, links: nextLinks } : prev;
    });
  }, [selectedNodeId, nodeDetail, graphData]);

  // Keep the rest of the UI aware of which managed nodes are now on the canvas.
  useEffect(() => {
    onRevealedIdsChange?.(injected.nodes.map((n) => n.id));
  }, [injected.nodes, onRevealedIdsChange]);

  useEffect(() => {
    userHasInteracted.current = false;
    setEngineStopped(false);
  }, [selectedType]);

  useEffect(() => {
    if (!graphRef.current || forcesApplied.current) return;
    forcesApplied.current = true;
    const g = graphRef.current;
    g.d3Force("charge")?.strength?.(-150);
    g.d3Force("link")?.distance?.(90);
  });

  const adjacency = useMemo(() => {
    const map = new Map<string, Set<string>>();
    const allLinks = [...(graphData?.edges ?? []), ...injected.links];
    for (const link of allLinks) {
      const src = linkEndId(link.source);
      const tgt = linkEndId(link.target);
      if (!map.has(src)) map.set(src, new Set());
      if (!map.has(tgt)) map.set(tgt, new Set());
      map.get(src)!.add(tgt);
      map.get(tgt)!.add(src);
    }
    return map;
  }, [graphData?.edges, injected.links]);

  const hubIds = useMemo(() => {
    if (!graphData?.nodes) return new Set<string>();
    const sorted = [...graphData.nodes].sort((a, b) => (b.degree ?? 0) - (a.degree ?? 0));
    return new Set(sorted.slice(0, HUB_LABEL_COUNT).map((n) => n.id));
  }, [graphData?.nodes]);

  const highlightSet = useMemo(() => {
    if (!selectedNodeId) return null;
    const set = new Set<string>([selectedNodeId]);
    adjacency.get(selectedNodeId)?.forEach((id) => set.add(id));
    return set;
  }, [selectedNodeId, adjacency]);

  useEffect(() => {
    if (!deepLinkNode || !graphData?.nodes || deepLinkApplied.current) return;
    const node = graphData.nodes.find(
      (n) => n.id === deepLinkNode || n.label === deepLinkNode || n.full_name === deepLinkNode,
    );
    if (node) {
      deepLinkApplied.current = true;
      setTimeout(() => {
        onNodeSelect(node);
        if (graphRef.current && node.x !== undefined) {
          graphRef.current.centerAt(node.x, node.y, 600);
          graphRef.current.zoom(3.5, 600);
        }
      }, 1000);
    }
  }, [deepLinkNode, graphData?.nodes, onNodeSelect]);

  const paintNode = useCallback(
    (node: any, ctx: CanvasRenderingContext2D, globalScale: number) => {
      const r = nodeRadius(node.degree);
      const isSelected = node.id === selectedNodeId;
      const isHovered = node.id === hoveredNodeId;
      const color = nodeColor(node.type);
      const isDimmed = highlightSet !== null && !highlightSet.has(node.id);

      if (isSelected) {
        ctx.beginPath();
        ctx.arc(node.x, node.y, r * 3.4, 0, 2 * Math.PI);
        ctx.fillStyle = `${color}1f`;
        ctx.fill();
      }

      const isRelationHovered = node.id === hoveredRelationNodeId && !isSelected;
      if (isRelationHovered) {
        // soft glow fill
        ctx.beginPath();
        ctx.arc(node.x, node.y, r * 3.2, 0, 2 * Math.PI);
        ctx.fillStyle = `${color}28`;
        ctx.fill();
        // dashed ring
        ctx.beginPath();
        ctx.arc(node.x, node.y, r * 3.0, 0, 2 * Math.PI);
        ctx.strokeStyle = color;
        ctx.lineWidth = 1.8 / globalScale;
        ctx.setLineDash([4 / globalScale, 3 / globalScale]);
        ctx.stroke();
        ctx.setLineDash([]);
      }

      if (!isSelected && isHovered && !isDimmed) {
        ctx.beginPath();
        ctx.arc(node.x, node.y, r * 1.8, 0, 2 * Math.PI);
        ctx.fillStyle = `${color}4d`;
        ctx.fill();
      }

      ctx.beginPath();
      ctx.arc(node.x, node.y, r, 0, 2 * Math.PI);
      ctx.fillStyle = isDimmed ? `${color}21` : color;
      ctx.fill();

      ctx.strokeStyle = isSelected
        ? "#3b82f6"
        : isDark
          ? `rgba(255,255,255,${isDimmed ? 0.04 : 0.18})`
          : `rgba(0,0,0,${isDimmed ? 0.04 : 0.22})`;
      ctx.lineWidth = isSelected ? 2.5 / globalScale : 1.1 / globalScale;
      ctx.stroke();

      // Managed-package components (revealed on click) get a dashed halo so
      // they're recognizable as third-party / un-editable nodes.
      if (node.is_managed && !isDimmed) {
        ctx.beginPath();
        ctx.arc(node.x, node.y, r + 2.5 / globalScale, 0, 2 * Math.PI);
        ctx.strokeStyle = isDark ? "rgba(255,255,255,0.55)" : "rgba(0,0,0,0.45)";
        ctx.lineWidth = 1 / globalScale;
        ctx.setLineDash([3 / globalScale, 2 / globalScale]);
        ctx.stroke();
        ctx.setLineDash([]);
      }

      const showLabel =
        isSelected ||
        isHovered ||
        (hubIds.has(node.id) && globalScale >= 0.8 && !isDimmed);
      if (showLabel) {
        const label = node.label ?? node.id;
        const fontSize = Math.max(10, 11 / globalScale);
        ctx.font = `${isSelected ? "bold " : ""}${fontSize}px Inter, sans-serif`;
        ctx.textAlign = "center";
        ctx.textBaseline = "top";
        const labelY = node.y + r + 3 / globalScale;
        ctx.fillStyle = isDark ? "rgba(0,0,0,0.6)" : "rgba(255,255,255,0.7)";
        ctx.fillText(label, node.x + 0.5 / globalScale, labelY + 0.5 / globalScale);
        ctx.fillStyle = isDimmed
          ? isDark ? "rgba(255,255,255,0.15)" : "rgba(0,0,0,0.15)"
          : isDark ? "#e2e8f0" : "#1e293b";
        ctx.fillText(label, node.x, labelY);
      }
    },
    [selectedNodeId, hoveredNodeId, hoveredRelationNodeId, highlightSet, hubIds, isDark],
  );

  const getLinkColor = useCallback(
    (link: any) => {
      const rel = link.rel ?? "";
      const base = linkColor(rel);
      const srcId = typeof link.source === "object" ? link.source.id : link.source;
      const tgtId = typeof link.target === "object" ? link.target.id : link.target;
      if (hoveredRelationNodeId) {
        const isHoveredEdge =
          (srcId === selectedNodeId && tgtId === hoveredRelationNodeId) ||
          (tgtId === selectedNodeId && srcId === hoveredRelationNodeId);
        if (isHoveredEdge) return base;
      }
      if (highlightSet) {
        const connected = highlightSet.has(srcId) && highlightSet.has(tgtId);
        return connected ? base : `${base}1a`;
      }
      return `${base}99`;
    },
    [highlightSet, hoveredRelationNodeId, selectedNodeId],
  );

  const getLinkWidth = useCallback(
    (link: any) => {
      const srcId = typeof link.source === "object" ? link.source.id : link.source;
      const tgtId = typeof link.target === "object" ? link.target.id : link.target;
      if (hoveredRelationNodeId) {
        const isHoveredEdge =
          (srcId === selectedNodeId && tgtId === hoveredRelationNodeId) ||
          (tgtId === selectedNodeId && srcId === hoveredRelationNodeId);
        if (isHoveredEdge) return 2.5;
      }
      if (!highlightSet) return 1;
      return highlightSet.has(srcId) && highlightSet.has(tgtId) ? 1.8 : 0.5;
    },
    [highlightSet, hoveredRelationNodeId, selectedNodeId],
  );

  const handleNodeClick = useCallback(
    (node: any) => onNodeSelect(node as Org360Node),
    [onNodeSelect],
  );

  const handleNodeHover = useCallback((node: any) => {
    const id = node?.id ?? null;
    setHoveredNodeId(id);
    onNodeHover?.(id);
  }, [onNodeHover]);

  const handleLinkHover = useCallback((link: any) => {
    setHoveredLink(!!link);
  }, []);

  const getLinkVisibility = useCallback(
    (link: any) => {
      if (!hiddenEdgeTypes?.size) return true;
      const rel = link.rel ?? "";
      for (const group of EDGE_TYPE_GROUPS) {
        if (group.key === "OTHER") continue;
        if (group.rels.includes(rel)) return !hiddenEdgeTypes.has(group.key);
      }
      return !hiddenEdgeTypes.has("OTHER");
    },
    [hiddenEdgeTypes],
  );

  const handleBackgroundClick = useCallback(() => {
    onNodeSelect(null);
    onCloseAll?.();
  }, [onNodeSelect, onCloseAll]);

  const handleMinimapNavigate = useCallback((gx: number, gy: number) => {
    graphRef.current?.centerAt(gx, gy, 400);
  }, []);

  const fgData = useMemo(
    () => ({
      nodes: [...(graphData?.nodes ?? []), ...injected.nodes],
      links: [...(graphData?.edges ?? []), ...injected.links],
    }),
    [graphData, injected],
  );

  useEffect(() => {
    if (!navigateToNodeId || !graphRef.current) return;
    const node = fgData.nodes.find((n: any) => n.id === navigateToNodeId);
    if (node && (node as any).x !== undefined) {
      graphRef.current.centerAt((node as any).x, (node as any).y, 600);
      graphRef.current.zoom(3.5, 600);
    }
  }, [navigateToNodeId, fgData.nodes]);

  const bgColor = isDark ? "#0f1117" : "#ffffff";

  return (
    <div
      ref={containerRef}
      className="absolute inset-0 overflow-hidden"
      onMouseDown={(e) => {
        setIsMouseDown(true);
        dragOrigin.current = { x: e.clientX, y: e.clientY };
        dragFiredRef.current = false;
      }}
      onMouseMove={(e) => {
        if (!dragOrigin.current || dragFiredRef.current || hoveredNodeId) return;
        const dx = e.clientX - dragOrigin.current.x;
        const dy = e.clientY - dragOrigin.current.y;
        if (Math.sqrt(dx * dx + dy * dy) > 8) {
          dragFiredRef.current = true;
          onCloseAll?.();
        }
      }}
      onMouseUp={() => {
        setIsMouseDown(false);
        dragOrigin.current = null;
      }}
      onMouseLeave={() => {
        setIsMouseDown(false);
        setHoveredLink(false);
        setHoveredNodeId(null);
        dragOrigin.current = null;
      }}
    >
      {isLoading && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-card/60 backdrop-blur-sm">
          <div className="flex flex-col items-center gap-3">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            <p className="text-sm text-muted-foreground font-mono">Loading graph…</p>
          </div>
        </div>
      )}

      {!isLoading && fgData.nodes.length === 0 && (
        <div className="absolute inset-0 z-10 flex items-center justify-center">
          <div className="flex flex-col items-center gap-3 text-center p-8 max-w-xs">
            {selectedType ? (
              <p className="text-sm text-muted-foreground">
                No relationship graph for{" "}
                <span className="font-mono font-semibold text-foreground">{selectedType}</span>.
                Its items are listed in the panel.
              </p>
            ) : (
              <>
                <p className="text-sm text-muted-foreground">No graph data for this org yet.</p>
                <button
                  type="button"
                  onClick={() => refetch()}
                  className="text-xs font-mono text-primary hover:underline"
                >
                  Try reloading
                </button>
              </>
            )}
          </div>
        </div>
      )}

      <ForceGraph2D
        ref={graphRef}
        width={dimensions.width}
        height={dimensions.height}
        graphData={fgData}
        backgroundColor={bgColor}
        nodeCanvasObject={paintNode}
        nodeCanvasObjectMode={() => "replace"}
        nodeLabel={(node: any) => `${node.type}: ${node.label ?? node.id}`}
        linkColor={getLinkColor}
        linkWidth={getLinkWidth}
        linkVisibility={getLinkVisibility}
        linkDirectionalParticles={0}
        onNodeClick={handleNodeClick}
        onNodeHover={handleNodeHover}
        onLinkHover={handleLinkHover}
        onBackgroundClick={handleBackgroundClick}
        onZoom={() => { userHasInteracted.current = true; }}
        d3AlphaDecay={0.022}
        d3VelocityDecay={0.38}
        cooldownTicks={200}
        onEngineStop={() => {
          if (!deepLinkNode && !userHasInteracted.current) graphRef.current?.zoomToFit(700, 60);
          setEngineStopped(true);
        }}
      />

      {/* Minimap — bottom-left, springs in after simulation settles */}
      <Org360Minimap
        nodes={fgData.nodes as Org360Node[]}
        links={fgData.links as any}
        selectedNodeId={selectedNodeId ?? null}
        visible={engineStopped && fgData.nodes.length > 0}
        onNavigate={handleMinimapNavigate}
      />

      {/* Toolbar — top-right */}
      <div className="absolute top-3 right-3 z-20 flex gap-1.5">
        <ToolbarTooltip label="Fit all nodes in view">
          <button
            type="button"
            onClick={() => graphRef.current?.zoomToFit(700, 60)}
            className={cn(
              "grid place-items-center w-8 h-8 rounded-lg border border-border",
              "bg-card/90 backdrop-blur-sm text-muted-foreground",
              "hover:bg-muted hover:text-foreground transition-colors duration-150",
            )}
          >
            <Scan className="h-3.5 w-3.5" />
          </button>
        </ToolbarTooltip>

        <ToolbarTooltip label="Reset graph">
          <button
            type="button"
            onClick={() => { onReset?.(); refetch(); }}
            className={cn(
              "grid place-items-center w-8 h-8 rounded-lg border border-border",
              "bg-card/90 backdrop-blur-sm text-muted-foreground",
              "hover:bg-muted hover:text-foreground transition-colors duration-150",
            )}
          >
            <RotateCcw className="h-3.5 w-3.5" />
          </button>
        </ToolbarTooltip>
      </div>
    </div>
  );
}
